import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Box,
  Typography,
  MenuItem,
} from "@mui/material";
import AutoCompleteTagInput from "../../../components/AutoCompleteTagInput/AutoCompleteTagInput";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BootstrapTooltip from "../../../components/BootstrapTooltip/BootstrapTooltip";

const isEmpty = (value) =>
  value === "" ||
  value === undefined ||
  value === null ||
  (Array.isArray(value) && value.length === 0);
const evaluateCondition = (currentValue, condition, targetValue) => {
  if (currentValue == null) return false;
  const curNum = Number(currentValue);
  const tarNum = Number(targetValue);
  const isNumeric = !isNaN(curNum) && !isNaN(tarNum);
  const compareNumeric = (fn) => isNumeric && fn(curNum, tarNum);
  const operators = {
    GREATER_THAN: () => compareNumeric((a, b) => a > b),
    LESS_THAN: () => compareNumeric((a, b) => a < b),
    GREATER_THAN_EQUAL: () => compareNumeric((a, b) => a >= b),
    LESS_THAN_EQUAL: () => compareNumeric((a, b) => a <= b),
    EQUALS: () => String(currentValue) === String(targetValue),
    NOT_EQUAL: () => String(currentValue) !== String(targetValue),
  };
  return operators[condition]?.() ?? false;
};

const getParentField = (depId, fields) =>
  fields.find(
    (f) =>
      String(f.input?.id) === String(depId) || String(f.id) === String(depId),
  );

const getParentValue = (parentField, values, idMapping, average) => {
  if (!parentField) return undefined;
  if (parentField.input?.type === "AVERAGE_SCORE") return average;

  const key = idMapping[String(parentField.input?.id)] || parentField.id;
  return values[key];
};

const isDependencySatisfied = (input, parentValue) => {
  if (input.dependentCondition) {
    return evaluateCondition(
      parentValue,
      input.dependentCondition,
      input.dependentValue,
    );
  }
  return !isEmpty(parentValue);
};
const getFriendlyCondition = (condition) => {
  const map = {
    EQUALS: "is equal to",
    NOT_EQUAL: "is not equal to",
    GREATER_THAN: "is greater than",
    LESS_THAN: "is less than",
    GREATER_THAN_EQUAL: "is greater than equal to",
    LESS_THAN_EQUAL: "is less than equal to",
  };
  return map[condition] || "matches the condition";
};

const buildDependencyMessage = (input, parentField) => {
  const label =
    parentField?.input?.label || parentField?.label || "another field";

  if (!input.dependentCondition) {
    return `This is required based on your answer in "${label}"`;
  }

  return `This is required because "${label}" ${getFriendlyCondition(
    input.dependentCondition,
  )} ${input.dependentValue}`;
};

const getFieldId = (fw) => fw?.id || fw?.tempId;

const normalizeOptions = (options) => {
  if (!options) return [];
  const raw = Array.isArray(options) ? options : options?.results || [];
  return raw.map((opt, idx) => {
    if (typeof opt === "string") return { label: opt, value: opt };
    if (typeof opt === "object" && opt !== null) {
      return {
        label: opt.label ?? opt.name ?? `(empty ${idx})`,
        value: opt.value ?? opt.code ?? opt.id ?? idx,
      };
    }
    return { label: String(opt), value: opt };
  });
};

const isValueChanged = (current, initial) => {
  if (Array.isArray(current) && Array.isArray(initial)) {
    return (
      current.length !== initial.length ||
      current.some((v, i) => v !== initial[i])
    );
  }
  return current !== initial;
};
const getValueOrDefault = (value, defaultValue) => {
  const isPopulated = value !== null && value !== undefined && value !== "";
  if (isPopulated) return value;
  return defaultValue ?? value;
};

const formatMultiValue = (field, backendValue) => {
  let arr = Array.isArray(backendValue) ? backendValue : [];
  if (!Array.isArray(backendValue) && backendValue) {
    arr = [backendValue];
  }

  if (arr.length === 0 && field?.defaultValue) {
    arr = Array.isArray(field.defaultValue)
      ? field.defaultValue
      : [field.defaultValue];
  }

  return field?.type === "DROPDOWN_MULTI_SELECT"
    ? arr.map((val) => (val != null ? String(val) : val))
    : arr;
};

const formatSingleValue = (field, backendValue) => {
  const val = Array.isArray(backendValue)
    ? (backendValue[0] ?? "")
    : (backendValue ?? "");
  const finalVal = getValueOrDefault(val, field?.defaultValue);

  if (field?.type === "DROPDOWN_SINGLE_SELECT") {
    return finalVal != null ? String(finalVal) : "";
  }

  if (field?.type === "NUMERIC_TEXT") {
    return finalVal !== null && finalVal !== undefined && finalVal !== ""
      ? finalVal
      : "";
  }

  return finalVal;
};

const parseInitialValue = (field, backendValue) => {
  if (!field) return backendValue;

  const isMulti = ["CHECKBOX_OPTIONS", "DROPDOWN_MULTI_SELECT"].includes(
    field.type,
  );

  return isMulti
    ? formatMultiValue(field, backendValue)
    : formatSingleValue(field, backendValue);
};

const getFieldError = (field, value) => {
  if (field?.required && field?.type !== "AVERAGE_SCORE" && isEmpty(value))
    return "This field is required";
  if (field?.type === "NUMERIC_TEXT" && value !== "" && isNaN(Number(value)))
    return "Must be a number";
  if (typeof value === "string" && value.includes("|"))
    return "Character '|' is not allowed";
  return null;
};

const getNumericRangeError = (value, min, max) => {
  if (isEmpty(value) || isNaN(Number(value))) return {};
  const num = Number(value);
  if (min != null && num < Number(min))
    return { error: true, text: `Min allowed: ${min}` };
  if (max != null && num > Number(max))
    return { error: true, text: `Max allowed: ${max}` };
  return {};
};

const scaleUIrender = (isScaleEnabled, tooltipText, maxValue, minValue) => {
  return (
    isScaleEnabled &&
    minValue === 1 &&
    maxValue === 5 && (
      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
        {`( ${tooltipText} )`}
      </Typography>
    )
  );
};

const FieldWrapper = ({
  label,
  description,
  required,
  children,
  error,
  readOnly,
  isScaleEnabled,
  tooltipText,
  maxValue,
  minValue,
}) => (
  <Box sx={{ mb: 1.2, width: "100%" }}>
    {label && (
      <Typography
        fontWeight={500}
        variant="body2"
        color={error ? "error" : "textPrimary"}
        sx={{ mb: 0.3 }}
      >
        {label} {required && !readOnly && "*"}
      </Typography>
    )}
    {
      <Typography
        variant="caption"
        display="block"
        sx={{
          mb: 0.5,
          color: "text.secondary",
          lineHeight: 1.2,
          fontSize: "0.75rem",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {description && description}
        {scaleUIrender(isScaleEnabled, tooltipText, maxValue, minValue)}
      </Typography>
    }
    {children}
  </Box>
);

const checkDependency = (
  depId,
  input,
  allValues,
  allFields,
  idMapping,
  average,
) => {
  const parentField = getParentField(depId, allFields);
  const parentValue = getParentValue(
    parentField,
    allValues,
    idMapping,
    average,
  );

  const conditionMet = isDependencySatisfied(input, parentValue);

  return {
    conditionMet,
    message: conditionMet ? buildDependencyMessage(input, parentField) : "",
  };
};

const isFieldRequired = (fw, allValues, allFields, average, idMapping = {}) => {
  const input = fw?.input;
  if (!input) return { status: false, message: "" };

  if (!input.dependentOnIds?.length) {
    return { status: !!input.required, message: "This field is required" };
  }

  let message = "";

  const status = input.dependentOnIds.every((depId) => {
    const result = checkDependency(
      depId,
      input,
      allValues,
      allFields,
      idMapping,
      average,
    );

    if (result.message) message = result.message;
    return result.conditionMet;
  });

  return { status, message };
};
const validateField = (fw, values, average, formFields, idMapping) => {
  const id = getFieldId(fw);
  const currentValue = fw.input.type === "AVERAGE_SCORE" ? average : values[id];

  if (fw.input.type !== "AVERAGE_SCORE" && !(id in values)) {
    return null;
  }

  const { status, message } = isFieldRequired(
    fw,
    values,
    formFields,
    average,
    idMapping,
  );

  const isMultiSelect = ["CHECKBOX_OPTIONS", "DROPDOWN_MULTI_SELECT"].includes(
    fw.input?.type,
  );
  const isEmptyValue = isMultiSelect
    ? !Array.isArray(currentValue) || currentValue.length === 0
    : isEmpty(currentValue);

  if (status && isEmptyValue) {
    return { id, error: message };
  }

  if (!isEmptyValue) {
    const standardError = getFieldError(fw.input, currentValue);
    if (standardError) return { id, error: standardError };

    if (fw.input.type === "NUMERIC_TEXT") {
      const range = getNumericRangeError(
        currentValue,
        fw.input.minValue,
        fw.input.maxValue,
      );
      if (range.error) return { id, error: range.text };
    }
  }

  return null;
};
// Externalize filter logic to avoid dynamic require and nesting
const getMultiSelectFilter = (options, params, showSelectAll = true) => {
  const { createFilterOptions } = require("@mui/material/useAutocomplete");
  const filter = createFilterOptions({
    matchFrom: "any",
    stringify: (option) => `${option.label} ${option.subLabel || ""}`,
  });
  const filtered = filter(options, params);

  if (options.length === 0) {
    return [{ label: "No options", value: "none", noOptions: true }];
  }

  // Only add "Select All" if the flag is true AND the user hasn't typed anything
  if (showSelectAll && params.inputValue === "") {
    return [{ label: "Select All", value: "all", all: true }, ...filtered];
  }
  return filtered;
};

// Sub-component for rendering individual list items
const MultiSelectOptionItem = ({ props, opt, isChecked }) => {
  if (opt.noOptions) {
    return (
      <Box {...props} className="auto-complete-no-options">
        <Box>{opt.label}</Box>
      </Box>
    );
  }

  return (
    <li
      {...props}
      key={opt.value}
      className={isChecked ? "multi-tag-edit-list-selected" : "multi-tag-style"}
    >
      <Box className="multi-tag-input-list-padding">
        <Checkbox
          className="auto-complete-checkbox-list"
          size="small"
          color="secondary"
          checked={isChecked}
        />
        <Box className="flex-options">
          <Box>{opt.label}</Box>
          {!opt.all && (
            <Typography variant="caption" className="filter-title">
              {opt.subLabel}
            </Typography>
          )}
        </Box>
      </Box>
    </li>
  );
};

// Helper to resolve which options array to use
const resolveMultiSelectOptions = (fw, projectOptions, userOptions) => {
  if (fw.input?.type === "PROJECT_NAME") return projectOptions;
  if (fw.input?.type === "PERFORMANCE_ASSESSED_BY") return userOptions;
  return normalizeOptions(fw.input?.options);
};

const renderScaleInput = ({
  fw,
  id,
  values,
  handleChange,
  readOnly,
  errors,
}) => {
  const { minValue, maxValue } = fw.input || {};
  const min = Number(minValue ?? 0);
  const max = Number(maxValue ?? 10);

  const scaleValues = [];
  for (let i = min; i <= max; i++) {
    scaleValues.push(String(i));
  }

  return (
    <Box>
      <RadioGroup
        row
        value={String(values[id] ?? "")}
        onChange={(e) => handleChange(id, e.target.value)}
        sx={{ gap: 1, flexWrap: "wrap" }}
      >
        {scaleValues.map((val) => (
          <FormControlLabel
            key={val}
            value={val}
            control={
              <Radio size="small" disabled={readOnly} color="secondary" />
            }
            label={val}
            className={String(values[id]) === val ? "selected-scale-item" : ""}
          />
        ))}
      </RadioGroup>

      {errors[id] && !readOnly && (
        <Typography variant="caption" color="error" sx={{ ml: 0 }}>
          {errors[id]}
        </Typography>
      )}
    </Box>
  );
};

const renderDefaultNumericInput = ({
  fw,
  id,
  values,
  handleChange,
  getCommonProps,
}) => {
  const handleNumericChange = (e) => {
    const val = e.target.value;
    if (val === "" || Number(val) >= 0) {
      handleChange(id, val);
    }
  };

  const handleNumericBlur = (e) => {
    const val = e.target.value;
    if (val !== "" && !isNaN(val)) {
      handleChange(id, parseFloat(val).toFixed(2));
    }
  };

  return (
    <TextField
      {...getCommonProps(fw, id)}
      type="number"
      inputProps={{ min: 0 }}
      value={values[id] ?? ""}
      onChange={handleNumericChange}
      onBlur={handleNumericBlur}
    />
  );
};

const FormFill = ({
  open,
  handleClose,
  formFields = [],
  formResponseId,
  selectedFormType,
  readOnly = false,
  userDetails = {},
  projectsList = [],
  preSelectedProjects = [],
  usersList = [],
  onSubmit,
  onAttemptClose,
}) => {
  const [values, setValues] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [errors, setErrors] = useState({});
  const isInitializedRef = useRef(false);
  const performanceTypes = [
    "CLASSROOM_ASSESSMENT_PERFORMANCE",
    "SHADOW_PROJECT_ASSESSMENT_PERFORMANCE",
  ];

  const tooltipText = performanceTypes.includes(selectedFormType)
    ? "1 - Poor*, 2 - Below Average*, 3 - Average*, 4 - Good, 5 - Excellent"
    : "1 - Never*, 2 - Rarely*, 3 - Not sure*, 4 - Sometimes, 5 - Always";

  const average = useMemo(() => {
    const averageFieldConfig = formFields.find(
      (f) => f.input?.type === "AVERAGE_SCORE",
    );
    const dependencies = averageFieldConfig?.input?.dependentOnIds || [];
    if (dependencies.length === 0) {
      return "";
    }
    const filledValues = formFields
      .filter(
        (f) =>
          f.input?.type === "NUMERIC_TEXT" &&
          dependencies.includes(f.input?.id),
      )
      .map((f) => {
        const fieldKey = getFieldId(f);
        return values[fieldKey];
      })
      .filter(
        (v) => v !== undefined && v !== null && v !== "" && !isNaN(Number(v)),
      )
      .map(Number);

    if (filledValues.length === 0) {
      return "";
    }
    const totalSum = filledValues.reduce((acc, curr) => acc + curr, 0);
    const calculatedAvg = totalSum / filledValues.length;

    return calculatedAvg.toFixed(2);
  }, [values, formFields]);

  const idMapping = useMemo(() => {
    const map = {};
    formFields.forEach((fw) => {
      const topLevelId = fw?.id || fw?.tempId;
      const inputId = fw?.input?.id;
      if (topLevelId && inputId) {
        map[inputId] = topLevelId;
      }
    });
    return map;
  }, [formFields]);

  const projectOptions = useMemo(() => {
    return (projectsList || []).map((project) => ({
      label: project.name,
      value: project.name,
      subLabel: project?.businessUnit?.name,
    }));
  }, [projectsList]);

  const userOptions = useMemo(() => {
    return (usersList || []).map((user) => ({
      label: user.fullName,
      value: user.email,
      subLabel: user?.email,
    }));
  }, [usersList]);
  useEffect(() => {
    if (!open) {
      isInitializedRef.current = false;
      setValues({});
      setInitialValues({});
      setErrors({});
      return;
    }
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const init = {};

    formFields.forEach((fw) => {
      if (!fw.input) return;

      const id = getFieldId(fw);
      let initialVal = parseInitialValue(fw.input, fw.value);
      if (fw.input?.type === "PROJECT_NAME" && preSelectedProjects.length > 0) {
        initialVal = preSelectedProjects.map((project) =>
          String(project.value),
        );
      }

      init[id] = initialVal;
    });

    setValues(init);
    setInitialValues(init);
    setErrors({});
  }, [open, formFields, preSelectedProjects]);

  useEffect(() => {
    if (!open || !isInitializedRef.current) return;
    if (Object.keys(values).length === 0 && formFields.length > 0) return;

    const nextErrors = {};

    formFields.forEach((fw) => {
      if (!fw.input) return;

      const result = validateField(fw, values, average, formFields, idMapping);

      if (result) {
        nextErrors[result.id] = result.error;
      }
    });

    setErrors(nextErrors);
  }, [values, formFields, idMapping, average, open]);

  const isDirty = useMemo(
    () =>
      Object.keys(values).some((k) =>
        isValueChanged(values[k], initialValues[k]),
      ),
    [values, initialValues],
  );

  const canSubmit = Object.keys(errors).length === 0;

  const handleChange = useCallback((id, value) => {
    setValues((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleCheckboxChange = useCallback((id, optionValue) => {
    setValues((prev) => {
      const current = Array.isArray(prev[id]) ? prev[id] : [];
      const next = current.includes(optionValue)
        ? current.filter((v) => v !== optionValue)
        : [...current, optionValue];
      return { ...prev, [id]: next };
    });
  }, []);

  const getCommonProps = (fw, id, isConditionallyRequired) => ({
    fullWidth: true,
    disabled: readOnly,
    size: "small",
    margin: "dense",
    color: "secondary",
    error: !readOnly && !!errors[id],
    helperText: !readOnly ? errors[id] : "",
    required: isConditionallyRequired && !readOnly,
    sx: { mt: 0 },
  });

  const fieldRenderers = {
    TEXT: (fw, id) => (
      <TextField
        {...getCommonProps(fw, id)}
        value={values[id] ?? ""}
        onChange={(e) => handleChange(id, e.target.value)}
      />
    ),
    TEXTAREA: (fw, id) => (
      <TextField
        {...getCommonProps(fw, id)}
        multiline
        minRows={2}
        maxRows={6}
        value={values[id] ?? ""}
        onChange={(e) => handleChange(id, e.target.value)}
      />
    ),
    NUMERIC_TEXT: (fw, id) => {
      const { options } = fw.input || {};
      let isScaleEnabled;
      if (readOnly) {
        isScaleEnabled =
          Array.isArray(options) && options.includes("scaleCheckBoxEnabled");
      } else {
        isScaleEnabled =
          Array.isArray(options?.results) &&
          options?.results.includes("scaleCheckBoxEnabled");
      }

      if (isScaleEnabled) {
        return renderScaleInput({
          fw,
          id,
          values,
          handleChange,
          readOnly,
          errors,
        });
      }

      return renderDefaultNumericInput({
        fw,
        id,
        values,
        handleChange,
        getCommonProps,
      });
    },

    AVERAGE_SCORE: (fw) => (
      <TextField
        fullWidth
        size="small"
        margin="dense"
        disabled
        type="text"
        value={average}
        color="secondary"
        sx={{ mt: 0, bgcolor: "rgba(0, 0, 0, 0.04)" }}
      />
    ),

    RADIO_OPTIONS: (fw, id) => (
      <Box>
        <RadioGroup
          row
          value={values[id] ?? ""}
          onChange={(e) => handleChange(id, e.target.value)}
        >
          {normalizeOptions(fw.input?.options).map((opt) => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={
                <Radio size="small" disabled={readOnly} color="secondary" />
              }
              label={opt.label}
            />
          ))}
        </RadioGroup>
        {errors[id] && !readOnly && (
          <Typography variant="caption" color="error" sx={{ ml: 0 }}>
            {errors[id]}
          </Typography>
        )}
      </Box>
    ),
    CHECKBOX_OPTIONS: (fw, id) => (
      <Box>
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          {normalizeOptions(fw.input?.options).map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={
                <Checkbox
                  size="small"
                  color="secondary"
                  disabled={readOnly}
                  checked={(values[id] ?? []).includes(opt.value)}
                  onChange={() => handleCheckboxChange(id, opt.value)}
                />
              }
              label={opt.label}
            />
          ))}
        </Box>
        {errors[id] && !readOnly && (
          <Typography variant="caption" color="error" sx={{ ml: 0 }}>
            {errors[id]}
          </Typography>
        )}
      </Box>
    ),
    DROPDOWN_SINGLE_SELECT: (fw, id) => (
      <TextField
        {...getCommonProps(fw, id)}
        select
        value={values[id] ?? ""}
        onChange={(e) => handleChange(id, e.target.value)}
      >
        {normalizeOptions(fw.input?.options).map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    ),
    DROPDOWN_MULTI_SELECT: (fw, id, isReq) => {
      const type = fw.input?.type;
      const options = resolveMultiSelectOptions(
        fw,
        projectOptions,
        userOptions,
      );

      const isSpecialField =
        type === "PROJECT_NAME" || type === "PERFORMANCE_ASSESSED_BY";
      const showSelectAll = !isSpecialField;

      const selected = Array.isArray(values[id]) ? values[id] : [];
      const selectedStrings = selected.map((val) => String(val));
      const errorMessage = !readOnly ? errors[id] : "";

      const handleMultiChange = (_, newVal) => {
        if (newVal.some((o) => o.all)) {
          const isAllSelected = selectedStrings.length === options.length;
          handleChange(
            id,
            isAllSelected ? [] : options.map((o) => String(o.value)),
          );
          return;
        }

        handleChange(
          id,
          newVal.filter((o) => !o.all).map((v) => String(v.value)),
        );
      };

      return (
        <Box className="width-100">
          <AutoCompleteTagInput
            {...getCommonProps(fw, id, isReq)}
            error={!!errorMessage}
            helperText={errorMessage}
            options={options}
            getOptionLabel={(o) => o?.label ?? ""}
            selectedValue={options.filter((o) =>
              selectedStrings.includes(String(o.value)),
            )}
            filterOptions={(opt, params) =>
              getMultiSelectFilter(opt, params, showSelectAll)
            }
            onChange={handleMultiChange}
            renderOption={(props, opt) => {
              const isChecked = opt.all
                ? selectedStrings.length === options.length
                : selectedStrings.includes(String(opt.value));
              return (
                <MultiSelectOptionItem
                  props={props}
                  opt={opt}
                  isChecked={isChecked}
                />
              );
            }}
          />
        </Box>
      );
    },
  };

  fieldRenderers.PROJECT_NAME = fieldRenderers.DROPDOWN_MULTI_SELECT;
  fieldRenderers.PERFORMANCE_ASSESSED_BY = fieldRenderers.DROPDOWN_MULTI_SELECT;

  return (
    <Dialog
      open={open}
      onClose={() =>
        isDirty && !readOnly ? onAttemptClose?.() : handleClose()
      }
      fullWidth
      maxWidth="lg"
      className="modal-drawer-container"
    >
      <DialogTitle className="dialog-title">
        {readOnly ? "Preview Form -" : "Fill Form For"} {userDetails.fullName} -{" "}
        {userDetails.bootcamp}
        <Box
          dangerouslySetInnerHTML={{ __html: userDetails.description }}
          sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap", fontSize:"13px", fontWeight:400, color:"#6b6b6b" }}
        />
      </DialogTitle>

      <DialogContent sx={{ mt: 1 }}>
        {formFields && formFields.length > 0 ? (
          <Box display="flex" flexDirection="column" gap={0.5}>
            {formFields.map((f) => {
              const id = getFieldId(f);
              const renderer = fieldRenderers[f.input?.type];
              const { status: isRequired } = isFieldRequired(
                f,
                values,
                formFields,
                average,
                idMapping,
              );
              const { options, maxValue, minValue } = f.input || {};
              let isScaleEnabled;
              if (readOnly) {
                isScaleEnabled =
                  Array.isArray(options) &&
                  options.includes("scaleCheckBoxEnabled");
              } else {
                isScaleEnabled =
                  Array.isArray(options?.results) &&
                  options?.results.includes("scaleCheckBoxEnabled");
              }
              return (
                <FieldWrapper
                  key={id}
                  label={f.label || f.input?.label}
                  description={f.input?.description}
                  required={isRequired}
                  readOnly={readOnly}
                  error={!readOnly && !!errors[id]}
                  isScaleEnabled={isScaleEnabled}
                  tooltipText={tooltipText}
                  minValue={minValue}
                  maxValue={maxValue}
                >
                  {renderer ? renderer(f, id) : null}
                </FieldWrapper>
              );
            })}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              textAlign: "center",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: "3rem", color: "#ccc", mb: 2 }} />
            <Typography
              variant="h6"
              color="textSecondary"
              sx={{ fontWeight: 500 }}
            >
              No input fields have been added
            </Typography>
            <Typography variant="body2" color="textSecondary">
              There is currently no content to display for this form.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "0rem 1.5rem 1.875rem" }}>
        <Button
          variant="outlined"
          color="secondary"
          className="block-button"
          onClick={handleClose}
        >
          <Typography variant="outlineBtnLabel">
            {readOnly ? "Close" : "Cancel"}
          </Typography>
        </Button>

        {!readOnly && (
          <Button
            disabled={!canSubmit}
            variant="contained"
            color="secondary"
            className="block-button"
            onClick={() => {
              const averageField = formFields.find(
                (f) => f.input?.type === "AVERAGE_SCORE",
              );
              const payloadValues = { ...values };
              if (averageField)
                payloadValues[getFieldId(averageField)] = average;

              const payload = {
                formResponseId,
                values: Object.entries(payloadValues).map(([id, v]) => ({
                  formInputResponseId: Number(id),
                  values: Array.isArray(v) ? v.map(String) : [String(v)],
                })),
              };
              onSubmit(payload);
              handleClose();
            }}
          >
            <Typography variant="outlineBtnLabel">Submit</Typography>
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

FormFill.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  formFields: PropTypes.array,
  formResponseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  readOnly: PropTypes.bool,
  userDetails: PropTypes.object,
  onSubmit: PropTypes.func,
  onAttemptClose: PropTypes.func,
  projectsList: PropTypes.array,
  preSelectedProjects: PropTypes.array,
  usersList: PropTypes.array,
};

export default FormFill;
